import React, { useEffect, useState } from 'react';
import { Axios } from '@helpers/ApiService';
import { EditModule, ApiResponse, EditRoleModalProps } from '@helpers/Models';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import { errorMessage } from '@helpers/Constants';
import { ModulePermission } from '@helpers/Models';
import { useInputValidation } from '@hooks/useInputValidation';
import { AxiosError } from 'axios';

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  isOpen,
  roleId,
  onClose,
}) => {
  const nameValidation = useInputValidation('textOnly', '');
  const [description, setDescription] = useState('');
  const [modules, setModules] = useState<EditModule[]>([]);
  const [selectedPermissions, setSelectedPermissions] =
    useState<ModulePermission>({});

  const [originalName, setOriginalName] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [originalPermissions, setOriginalPermissions] =
    useState<ModulePermission>({});

  const { showToastMessage } = useToast();

  useEffect(() => {
    if (!isOpen || !roleId) {
      return;
    }

    fetchModules();
    fetchRoles();
  }, [isOpen, roleId]);

  /**
   * Fetches role data from the server and updates the component state with the fetched data.
   *
   * This function sends a POST request to the `FETCH_ROLES` endpoint with the `roleId` as the payload.
   * On successful response, it updates the state with the role's name, description, and permissions.
   *
   * The permissions are formatted into a `ModulePermission` object and set in the `selectedPermissions` state.
   *
   * In case of an error during the fetch operation, a toast message is displayed with an error message.
   *
   * @async
   * @function fetchRoles
   * @returns {Promise<void>} A promise that resolves when the role data has been fetched and the state has been updated.
   */
  const fetchRoles = async (): Promise<void> => {
    try {
      const response = await Axios.post<ApiResponse>(ApiEndpoints.FETCH_ROLES, {
        id: roleId,
      });
      const roleData = response.data.data[0];

      nameValidation.setValue(roleData.name);
      setDescription(roleData.desc);
      setOriginalName(roleData.name);
      setOriginalDescription(roleData.desc);

      // Format permissions into the selectedPermissions state
      const formattedPermissions = Array.isArray(roleData.permissions)
        ? roleData.permissions.reduce(
            (
              acc: ModulePermission,
              moduleData: { id: number; name: string },
            ) => {
              acc[moduleData.id] = {
                moduleName: moduleData.name,
                permissions: {
                  all: true,
                },
              };
              return acc;
            },
            {} as ModulePermission,
          )
        : {};

      setSelectedPermissions(formattedPermissions);
      setOriginalPermissions(formattedPermissions);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_ROLE, 'error');
    }
  };

  /**
   * Fetches the list of modules from the server.
   *
   * This function sends a POST request to the FETCH_MODULES endpoint
   * and updates the state with the retrieved modules data.
   * If an error occurs during the fetch, it displays an error toast message.
   *
   * @returns {Promise<void>} A promise that resolves when the fetch is complete.
   */
  const fetchModules = async (): Promise<void> => {
    try {
      const moduleResponse = await Axios.post(ApiEndpoints.FETCH_MODULES, {});
      setModules(moduleResponse.data.data);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_MODULES, 'error');
    }
  };

  /**
   * Handles the change event for a checkbox, toggling the permission value for a given module.
   */
  const handleCheckboxChange = (moduleId: string, permission: string): void => {
    setSelectedPermissions((prev) => {
      const updatedPermissions = JSON.parse(JSON.stringify(prev));

      // If module doesn't exist, create it with the module name from the modules array
      if (!updatedPermissions[moduleId]) {
        const module = modules.find(
          (m) => m.id.toString() === moduleId.toString(),
        );
        updatedPermissions[moduleId] = {
          moduleName: module ? module.name : '',
          permissions: {},
        };
      }

      // Toggle the permission value
      updatedPermissions[moduleId].permissions[permission] =
        !updatedPermissions[moduleId].permissions[permission];

      return updatedPermissions;
    });
  };

  /**
   * Checks if the form has been changed.
   */
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    // Validate name input
    if (!nameValidation.isValid || !nameValidation.value.trim()) {
      showToastMessage(
        'Please enter a valid role name with only letters and spaces.',
        'error',
      );
      return;
    }

    // Prepare the modules array with the correct structure
    const formattedModules = modules
      .filter((module) => selectedPermissions[module.id]?.permissions.all)
      .map((module) => ({
        id: module.id,
        permissions: {
          read: true,
          write: true,
          edit: true,
          delete: true,
        },
      }));

    const payload = {
      id: roleId,
      name: nameValidation.value.trim(),
      description: description.trim(),
      modules: formattedModules,
    };

    try {
      await Axios.post(ApiEndpoints.UPSERT_ROLE, payload);
      showToastMessage('Role updated successfully', 'success');
      onClose();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        showToastMessage(error.response.data.errors[0].message, 'error');
      } else {
        showToastMessage(errorMessage.ERROR_CREATING_ROLE, 'error');
      }
    }
  };

  const isFormChanged = (): boolean => {
    const trimmedName = nameValidation.value.trim();
    const trimmedDescription = description.trim();
    const trimmedOriginalName = originalName.trim();
    const trimmedOriginalDescription = originalDescription.trim();

    // Name is required - if empty, disable button
    if (trimmedName === '') {
      return false;
    }

    // Check if at least one permission is selected
    const hasAnyPermission = Object.values(selectedPermissions).some(
      (module) => module.permissions.all === true,
    );

    if (!hasAnyPermission) {
      return false;
    }

    // Check if name or description changed
    const isNameChanged = trimmedName !== trimmedOriginalName;
    const isDescriptionChanged =
      trimmedDescription !== trimmedOriginalDescription;

    // Check if any permission changed
    const isPermissionChanged = (() => {
      // Get all unique module IDs from both selected and original permissions
      const allModuleIds = new Set([
        ...Object.keys(selectedPermissions),
        ...Object.keys(originalPermissions),
      ]);

      // Check if any permission has changed
      return Array.from(allModuleIds).some((moduleId) => {
        const selectedPermission =
          selectedPermissions[moduleId]?.permissions.all || false;
        const originalPermission =
          originalPermissions[moduleId]?.permissions.all || false;
        return selectedPermission !== originalPermission;
      });
    })();

    return isNameChanged || isDescriptionChanged || isPermissionChanged;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-bold">Edit Role</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Role Name */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Role Title *
            </label>
            <input
              type="text"
              value={nameValidation.value}
              onChange={nameValidation.handleChange}
              onKeyPress={nameValidation.handleKeyPress}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              required
            />
            {nameValidation.error && (
              <p className="text-red-500 text-xs mt-1">
                {nameValidation.error}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              rows={3}
            />
          </div>

          {/* Permissions Table */}
          <div className="mt-6">
            <div className="grid grid-cols-2 font-medium border-b pb-2">
              <span>Modules</span>
              <span className="text-center">Permissions</span>
            </div>

            {modules.map((module) => (
              <div key={module.id} className="grid grid-cols-2 py-2 border-b">
                <span>{module.name}</span>
                <div className="flex gap-4 justify-center">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={
                        selectedPermissions[module.id]?.permissions.all || false
                      }
                      onChange={() => handleCheckboxChange(module.id, 'all')}
                      className="w-4 h-4 rounded accent-primary"
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-6 py-2 h-10 rounded-md text-white ${
                isFormChanged() ? 'bg-primary' : 'bg-gray-400'
              }`}
              disabled={!isFormChanged()}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRoleModal;
